<template>
    <div>
        <div v-if="completed && !readOnly && latestUserSubmission" class="columns">
            <div class="column">
                <p class="has-text-subtle">
                    Completed
                    {{ formatDate(latestUserSubmission.created_at, 'LLL zz') }}
                </p>
            </div>
        </div>

        <div
            v-if="
                $gate.allows('viewAny', ['ActivitySubmission', course]) ||
                    $gate.allows('viewOther', ['ActivitySubmission', course]) ||
                    hasMoreDetail()
            "
            class="columns is-multiline"
        >
            <div class="column is-12">
                <div class="content">
                    <h2 class="m-b-none tw:text-[var(--p-text-color)]!">My Submissions</h2>
                </div>
            </div>

            <div v-if="submissions.length > 0 && !submissionsLoading" class="column card-list is-compact">
                <div v-for="submission in userSubmissions" :key="submission.id" class="card">
                    <header class="card-header">
                        <div class="card-header-title">
                            <div class="tw:flex tw:w-full tw:justify-start tw:flex-wrap tw:gap-2">
                                <div class="tw:flex tw:gap-2">
                                    <div v-if="allowsMultipleSubmissions">
                                        <e-tag class="has-text-weight-normal">
                                            {{ submission.attempt }} /
                                            {{ maxAttempts }}
                                        </e-tag>
                                    </div>
                                    <div>
                                        <e-tag class="has-text-weight-normal">
                                            {{ formatDate(submission.created_at, 'LLL zz') }}
                                        </e-tag>
                                    </div>
                                </div>

                                <div class="tw:flex tw:sm:flex-col tw:gap-2 tw:justify-items-center">
                                    <div v-if="submissionIsGraded(submission) && hasMoreDetail()">
                                        <e-button
                                            type="is-info"
                                            outlined
                                            tag="router-link"
                                            :to="gradeLink"
                                            size="is-small"
                                        >
                                            View Grade
                                        </e-button>
                                    </div>

                                    <div class="tw:flex tw:gap-2">
                                        <div v-if="get(submission, 'attempt_authorizations', []).length > 0">
                                            <e-tag type="is-warning" class="has-text-weight-normal">
                                                Authorized Attempt
                                            </e-tag>
                                        </div>
                                        <div>
                                            <div v-if="submission.is_late">
                                                <e-tag type="is-danger" class="has-text-weight-normal"> Late</e-tag>
                                            </div>

                                            <div v-else>
                                                <e-tag type="is-success" class="has-text-weight-normal"> On-Time</e-tag>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="canDelete(submission)">
                                        <e-button type="is-danger is-outlined" @click="confirmDelete(submission)">
                                            <e-icon icon="trash-alt" />
                                        </e-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <div v-if="hasMoreDetail(submission)" class="card-content">
                        <div class="content">
                            <slot name="content" :submission="submission" />
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="submissionsLoading" class="column">
                <skeleton-card :active="submissionsLoading" :content="{ display: skeletonType }" />
            </div>

            <div v-if="isEmpty(submissions) && !submissionsLoading" class="column">
                <p class="has-text-subtle">No submissions to display.</p>
            </div>
        </div>
    </div>
</template>

<script>
import find from 'lodash/find.js';
import get from 'lodash/get.js';
import { useI18n } from 'vue-i18n';

import SkeletonCard from '@/components/skeleton-loader/skeleton-card/SkeletonCard.vue';
import { ACTIVITY_TYPES } from '@/constants/ActivityTypes.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';

export default {
    name: 'ActivitySubmissions',

    components: { SkeletonCard },

    mixins: [CourseActivityMixin],

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        activityType() {
            return this.activity.activity_type;
        },

        admin() {
            return this.$store.getters['User/hasRole']('Admin');
        },

        isActivityPastDue() {
            const dueAt = this.activity?.activity_settings?.due_at;

            if (!dueAt) {
                return false;
            }

            return this.$moment.utc(dueAt).isBefore(this.$moment.utc());
        },

        gradeLink() {
            return {
                name: 'student-grade-view',
                params: {
                    courseId: this.$route.params.courseId,
                    userId: this.$store.getters['Auth/userId'],
                },
            };
        },

        skeletonType() {
            return !['view-information', 'acknowledge-information'].some(
                check => check === this.activity.activity_type
            );
        },
    },

    methods: {
        get,

        hasMoreDetail(submission = false) {
            if (this.activityType === ACTIVITY_TYPES.SURVEY) {
                return [ACTIVITY_TYPES.FILE_SUBMISSION, ACTIVITY_TYPES.SURVEY].includes(this.activityType);
            }

            if (submission && this.activityType === ACTIVITY_TYPES.QUIZ) {
                return (
                    this.activityType === ACTIVITY_TYPES.QUIZ && get(submission, 'submittable.quiz_attempt.total_score')
                );
            }

            if (this.activityType === ACTIVITY_TYPES.ACKNOWLEDGE_INFORMATION) {
                return submission && get(submission, 'submittable.media', []).length > 0;
            }

            return [ACTIVITY_TYPES.FILE_SUBMISSION, ACTIVITY_TYPES.LTI_TOOL].includes(this.activityType);
        },

        canDelete(submission) {
            if (this.isActivityPastDue) {
                return false;
            }

            if (this.submissionIsGraded(submission)) {
                return false;
            }

            const canDelete = this.$gate.allows('delete', ['ActivitySubmission', this.course]);

            const owner = this.isOwnSubmission(submission);

            if (this.activityType !== 'quiz') {
                return canDelete && owner;
            }

            return this.admin;
        },

        confirmDelete(submission) {
            this.$dialog.confirm({
                title: this.translate('deleting_submission'),
                message: this.translate('deleting_submission_confirmation'),
                confirmText: this.translate('delete'),
                type: 'is-danger',
                onConfirm: async () => {
                    this.submissionsLoading = true;

                    try {
                        const result = await this.$store.dispatch(
                            'ActivitySubmissions/deleteActivitySubmission',
                            submission
                        );

                        const isAxiosError = result instanceof Error;
                        const hasHttpError = result?.response?.status >= 400;
                        const hasApiError = result?.response?.data?.code >= 400;

                        if (isAxiosError || hasHttpError || hasApiError) {
                            const errorMessage =
                                result?.response?.data?.message ||
                                result?.message ||
                                'Unable to delete this submission. Please try again.';

                            this.$edverumToast.open({
                                duration: 5000,
                                message: errorMessage,
                                position: 'is-top',
                                type: 'is-danger',
                            });

                            this.submissionsLoading = false;
                            return;
                        }

                        await this.fetchSubmissions();
                        await this.$store.dispatch('Activities/fetchOne', this.activity.id);
                        this.verifyActivity();

                        this.$edverumToast.open({
                            duration: 3000,
                            message: 'Submission deleted successfully.',
                            position: 'is-top',
                            type: 'is-success',
                        });
                    } catch (error) {
                        const errorMessage =
                            error?.response?.data?.message || 'An error occurred while deleting the submission.';

                        this.$edverumToast.open({
                            duration: 5000,
                            message: errorMessage,
                            position: 'is-top',
                            type: 'is-danger',
                        });
                    } finally {
                        this.submissionsLoading = false;
                    }
                },
            });
        },

        isOwnSubmission(submission) {
            return Number(submission.created_by) === Number(this.$store.getters['Auth/userId']);
        },

        submissionIsGraded(submission) {
            return find(this.userGrades, userGrade => userGrade.activity_submission_id === submission.id);
        },
    },
};
</script>
